import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppShell as UIAppShell, ConfirmModal, marketingMenuColumns, useTheme, type AppShellTopBarProps } from '@wehunt/ui'
import { useAuth } from '@wehunt/core-web'
import { ChronicleNpsPrompt } from '@wehunt/chronicle-web-wehunt'
import { shell } from './router'
import { isFeatureEnabled } from './appConfig'
import { buildShopNavItems, readShopLinks, shopBaseUrl, shopCartHref, shopSearchHref } from './shopLinks'
import { TOP_BAR_LANGUAGES, isPickerLanguage, pickerLanguage } from './topBarLocale'

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { t, i18n } = useTranslation('nav')
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false)

  // Call each registered badge hook in stable order. The provider list is
  // fixed for the lifetime of composeStreams, which keeps the loop safe
  // under React's rules of hooks. Adding/removing contributions at runtime
  // is explicitly unsupported.
  const lastKnown = useRef<Map<string, number>>(new Map())
  const badgeValues = new Map<string, number | undefined>()
  for (const provider of shell.badgeProviders) {
    const key = `${provider.section}:${provider.itemId}`
    const value = provider.useBadge()
    if (value !== undefined) {
      // Cache the most recent non-undefined value so a background refetch
      // never blanks the badge mid-flight.
      lastKnown.current.set(key, value)
    }
    badgeValues.set(key, value ?? lastKnown.current.get(key))
  }

  // Build a stable memo key from the resolved values so React only
  // recomputes navSections when something actually changes.
  const memoKey = Array.from(badgeValues.entries())
    .map(([k, v]) => `${k}=${v ?? ''}`)
    .join('|')
  const rawNavSections = useMemo(
    () => shell.navSections({ values: badgeValues }),
    // The memoKey collapses all badge entries into a single primitive —
    // disabling exhaustive-deps because depending on the Map itself would
    // bust the memo every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memoKey],
  )

  // Nav labels come from the stream contributions as English config literals, so
  // they're translated here (the app layer) rather than in the shared, i18n-
  // agnostic Sidebar. Items translate by their stable id; unknown ids fall back
  // to their English label so a new stream still renders.
  //
  // composeStreams emits two sections: the unlabelled main section and the
  // labelled account section. This is where they become the menu shared with
  // the web shop: the main section gets the WEB APP heading, and the account
  // section gets the shop's items — built from runtime config and the UI
  // language, not from a stream contribution, because they leave this app —
  // ahead of whatever the streams contribute there (Settings). The links carry
  // the member's light/dark choice, so they are rebuilt when it flips.
  const { theme } = useTheme()
  const shopBase = shopBaseUrl(readShopLinks(), i18n.language)
  const shopItems = useMemo(
    () =>
      buildShopNavItems(readShopLinks(), i18n.language, (id) => t(`items.${id}`), {
        theme,
        comingSoonLabel: t('comingSoon'),
      }),
    [i18n.language, t, theme],
  )
  const navSections = useMemo(
    () =>
      rawNavSections.map((section, index) => {
        const isAccount = Boolean(section.label)
        const items = section.items.map((item) => ({
          ...item,
          label: t(`items.${item.id}`, { defaultValue: item.label }),
        }))
        return {
          label: isAccount ? t('sections.account') : index === 0 ? t('sections.webApp') : undefined,
          items: isAccount ? [...shopItems, ...items] : items,
        }
      }),
    [rawNavSections, shopItems, t],
  )

  // The bar is the one from the prototype and wehunt.app: search, cart and the
  // language flag on the pill, the marketing tree behind the hamburger. Search
  // and the cart are the shop's, so like the account items they appear only
  // when a shop is configured, and they carry the theme across the same way.
  // The account pill is off: the rail already is the account menu here. The
  // picker is language-only — the shop chooses the market, this app only
  // speaks a language — and writes through i18next, whose detector persists
  // the choice exactly as the settings page does.
  const topBar = useMemo<AppShellTopBarProps>(
    () => ({
      variant: 'full',
      showAccount: false,
      cartHref: shopBase ? shopCartHref(shopBase, theme) : undefined,
      onSearchSubmit: shopBase
        ? (query: string) => window.location.assign(shopSearchHref(shopBase, query, theme))
        : undefined,
      locale: { country: 'GLOBAL', language: pickerLanguage(i18n.language) },
      languages: TOP_BAR_LANGUAGES,
      showLocaleCountry: false,
      onLocaleChange: ({ language }: { language: string }) => {
        if (isPickerLanguage(language)) void i18n.changeLanguage(language)
      },
      searchLabels: {
        dialog: t('topBar.search'),
        placeholder: t('topBar.searchPlaceholder'),
        close: t('topBar.closeSearch'),
        downloadTitle: t('topBar.downloadApp'),
      },
      // The marketing menu in the UI language, each link to that language's
      // page on wehunt.app.
      menuColumns: marketingMenuColumns(i18n.language, (key) => t(`topBar.marketing.${key}`)),
      menuLabels: { dialog: t('topBar.menu'), downloadTitle: t('topBar.downloadApp') },
      indexMenuLabels: {
        dialog: t('topBar.menu'),
        downloadTitle: t('topBar.downloadApp'),
        railSection: t('sections.account'),
        signOut: t('signOut'),
      },
      localeLabels: {
        title: t('topBar.language'),
        language: t('topBar.language'),
        cancel: t('topBar.cancel'),
        apply: t('topBar.apply'),
        close: t('topBar.close'),
      },
    }),
    [shopBase, theme, i18n, t],
  )

  // Just clear the Firebase session. The redirect to /sign-in is handled
  // reactively in the router's RootComponent once auth becomes signed-out, so
  // we don't navigate here — navigating imperatively while Firebase's async
  // onAuthStateChanged is still propagating races the router mid-transition
  // and crashes the app (CLUB-209).
  return (
    <>
      <UIAppShell
        currentPath={pathname}
        navSections={navSections}
        // Named for the surface each one sits on, not for the ink it uses: the
        // on-light file is brand green and the on-dark file near-white. Passed
        // explicitly rather than left to the shared TopBar default so the brand
        // choice reads at the composition root, where it belongs.
        logo="/assets/wehunt-logo-on-light.svg"
        logoDark="/assets/wehunt-logo-on-dark.svg"
        logoAlt="WeHunt"
        onSignOut={() => setSignOutConfirmOpen(true)}
        onSidebarNavigate={(href) => navigate({ to: href })}
        sidebarLabels={{ signOut: t('signOut'), closeMenu: t('closeMenu') }}
        topBarAriaLabels={{
          openNav: t('openNav'),
          openMenu: t('topBar.openMenu'),
          closeMenu: t('topBar.closeMenu'),
          search: t('topBar.search'),
          cart: t('topBar.cart'),
          locale: t('topBar.selectLanguage'),
        }}
        topBar={topBar}
        topBarShowSearch={Boolean(shopBase)}
        topBarShowCart={Boolean(shopBase)}
      >
        {children}
      </UIAppShell>
      {signOutConfirmOpen && (
        <ConfirmModal
          title={t('signOutConfirm.title')}
          description={t('signOutConfirm.description')}
          confirmLabel={t('signOutConfirm.confirm')}
          cancelLabel={t('signOutConfirm.cancel')}
          onConfirm={() => {
            setSignOutConfirmOpen(false)
            void signOut()
          }}
          onCancel={() => setSignOutConfirmOpen(false)}
        />
      )}
      {/* Chronicle owns the NPS prompt end to end; the shell only mounts it, as
          it does the sign-out confirmation above. StreamContribution has no
          overlay slot, so a direct mount is the composition point — and the
          composition root is also where flags resolve, so the domain receives a
          decision rather than importing appConfig.

          Gated by rendering rather than by a prop so that OFF means the
          component never mounts: no eligibility request, no dwell timer, nothing
          for a bad rollout to do. Resolved per render (not at module scope like
          router.tsx's route flags) because this is a render-time decision and
          isFeatureEnabled is a synchronous window read. */}
      {isFeatureEnabled('chronicleNpsPrompt') && <ChronicleNpsPrompt />}
    </>
  )
}
